import { computed, effect, ElementRef, Injector, Signal, signal } from "@angular/core";
import { CommonField, FieldAndValue } from "../field";


interface FieldWidgetInterface<T, FIELD extends CommonField<unknown, T>> {
	formFieldName: Signal<string>,
	fieldAndValue: Signal<FieldAndValue<T, FIELD>>,
	getValidValue(): {} | undefined,
	reset(): void;
	setFocus(): void,
}


export type OnSubmit<VALUES> = (values: VALUES, changed: string[]) => Promise<void>;


/**
 * The CommonForm groups one or more common fields so they can be
 * checked for completion and/or errors as one unit. To use it, 
 * instatiate a CommonForm and pass it to each field using the
 * CommonField.form property.
 * 
 * Note: Common Fields can each have a save method which will work with
 *       or without the use of a CommonForm.  Alternatively, all changes
 *       can be built up and saved together in a form button click event.
 * 
 * Set up a button by passing it to setSubmitButton().  The form automatically
 * adds btn-primary when the form is ready, and removes it otherwise.  Upon
 * clicking, the form will check for errors and force the fields to display
 * the errors.  If not errors, the onSubmit function passed in the constructor
 * will be called with all the latest values.
 * 
 */
export class CommonForm<VALUES = {}> {

	private readonly _widgets = signal<FieldWidgetInterface<unknown, CommonField<unknown, unknown>>[]>([]);

	constructor(injector: Injector) {

		effect(() => {

			const button = this._submitButton();
			if (!button) return;

			const values = this.getValidatedFieldValues();
			const classList = button.nativeElement.classList;

			//
			// Remove all current classes
			//
			while (classList.length > 0) {
				const className = classList.item(0);
				classList.remove(className!);
			}

			//
			// Add btn-primary only if we have values (no errors)
			//
			classList.add('btn');
			if (values) classList.add('btn-primary');
			else if (this.forceShowErrors()) classList.add('btn-danger');

		}, { injector });

	}

	/**
	 * If true, the fields will show errors even of they
	 * haven't had focus.
	 */
	public readonly forceShowErrors = signal(false);


	/**
	 * Processes all field values. If all are valid, returns
	 * an object with all the property values, else undefined.
	 */
	private getValidatedFieldValues(): VALUES | undefined {

		const widgets = this._widgets();
		let allValid = true;
		let values: any = {};

		for (const widget of widgets) {

			const { field } = widget.fieldAndValue();
			if (field?.isExcluded && field.isExcluded()) {
				continue;
			}

			const validValue = widget.getValidValue();
			if (validValue === undefined) allValid = false;
			else values = { ...values, ...validValue };

		}

		if (allValid) return values as VALUES;
		else return undefined;

	}


	private _submitButton = signal<ElementRef<HTMLButtonElement> | undefined>(undefined);


	/**
	 * Returns true if the CommonForm.setSubmitButton() function was called.
	 */
	public readonly hasSubmitButton = computed(() => {
		return this._submitButton() !== undefined;
	});


	/**
	 * Add a button with an id like #formSubmitButton and inject it as a viewChild.
	 * IMPORTANT: Call this function in ngAfterViewInit so the viewChild is ready.
	 * 
	 * @param button - The button object, injected with viewChild.required<...>().
	 * 
	 * @param onSubmit - The function to call after a click with no errors.
	 */
	setSubmitButton(
		button: ElementRef<HTMLButtonElement>,
		onSubmit: OnSubmit<VALUES> = async (values: VALUES, changed: string[]) => {},
	) {

		const oldSubmitButton = this._submitButton();

		if (oldSubmitButton) {
			if (oldSubmitButton.nativeElement.isConnected) {
				console.error(`setSubmitButton was called with a new button but there was a prior button and it is still in the DOM.`);
			}
		}


		this._submitButton.set(button);


		//
		// Add a click event that checks each field for any errors.
		// For each error, force the field to show the error.
		// If no errors were found then call onSubmit.
		//
		button.nativeElement.addEventListener('click', () => {

			this.forceShowErrors.set(true);

			const values = this.getValidatedFieldValues();

			//
			// There are errors
			//
			if (!values) return;

			//
			// There aren't errors
			//
			const changed = this._widgets()
				.filter(widget => widget.fieldAndValue().value.isChanged)
				.map(widget => widget.formFieldName());

			onSubmit(values, changed);
		});
	}


	/**
	 * Each Common Field Widget calls the OnInit function in the base class,
	 * which registers the field with the form by calling addField()
	 */
	addFieldWidget<T, FIELD extends CommonField<unknown, T>>(
		widget: FieldWidgetInterface<T, FIELD>,
	) {

		//
		// Add it to the array, removing any prior with the same fieldName
		//
		const widgets = this._widgets().filter(w => w.formFieldName !== widget.formFieldName);
		const newWidgets: FieldWidgetInterface<unknown, CommonField<unknown, unknown>>[] = [];
		newWidgets.push(...widgets);
		newWidgets.push(widget as FieldWidgetInterface<unknown, CommonField<unknown, unknown>>);
		this._widgets.set(newWidgets);
	}


	/**
	 * Loops through each field, removes any ending pendingValue, and recalculates. 
	 * 
	 * @param forceShowErrors Sets the field to think it had focus in the past
	 *                 or not.  If true, any error will be displayed, even
	 *                 if the error was from a validation against actualValue.
	 */
	public resetAll(forceShowErrors = false) {

		this.forceShowErrors.set(forceShowErrors);
		const widgets = this._widgets();

		for (const widget of widgets) {
			widget.reset();
		}
	}


	/**
	 * @deprecated USE setSubmitButton() AND THE submitted EVENT INSTEAD!
	 * 
	 * Returns true if all required fields have a value
	 * AND none of the fields have a validation error.
	 */
	public readonly allValidWithoutError = computed(() => {

		const values = this.getValidatedFieldValues();
		return !!values;

	});

}
